import { moNum } from "@/lib/moNum";
import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { Link } from "react-router-dom";
import { Warehouse, Loader2, Search, History, Plus, Minus, Printer, AlertTriangle, Tag, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import FGShelfView from "@/components/FGShelfView";
import CreateMOPopover from "@/components/CreateMOPopover";
import { printInventoryReport } from "@/components/inventory/inventoryPrint";
import { printPalletTags, downloadPalletTags, TAG_SIZES } from "@/lib/palletTagPrint";
import * as XLSX from "xlsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SOReservationsDialog } from "@/components/inventory/SOReservationsDialog";
import { SalesHistoryDialog } from "@/components/inventory/SalesHistoryDialog";

const PALETTE = ["#002FA7", "#0E7C7B", "#B65C24", "#6D28D9", "#0F766E", "#9D174D", "#3F6212", "#B45309"];

const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const SHELF_LIFE_YEARS = 2;
const expiryOf = (s) => {
  if (!s) return null;
  const d = new Date(s.length <= 10 ? s + "T00:00:00" : s);
  if (isNaN(d)) return null;
  d.setFullYear(d.getFullYear() + SHELF_LIFE_YEARS);
  return d;
};
const expiryMeta = (produced) => {
  const d = expiryOf(produced);
  if (!d) return { text: "—", cls: "text-slate-400" };
  const now = new Date();
  const days = Math.ceil((d - now) / 86400000);
  const text = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  if (days < 0) return { text: `${text} · Expired`, cls: "font-semibold text-red-600" };
  if (days <= 90) return { text: `${text} · ${days}d left`, cls: "font-semibold text-amber-600" };
  return { text, cls: "text-slate-600" };
};

// Same color rules, but for a server-computed expiration date (per batch).

// Popover to add or subtract finished-goods stock in cases or pallets.
function FGStockPopover({ mode, casesPerPallet, onApply, testId }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("cases");
  const isSub = mode === "subtract";
  const Icon = isSub ? Minus : Plus;
  const per = unit === "pallets" ? (casesPerPallet || 1) : 1;
  const delta = (parseFloat(qty) || 0) * per;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`rounded-sm border border-slate-200 p-1 hover:bg-slate-100 ${isSub ? "text-slate-500 hover:text-red-600" : "text-[#002FA7]"}`} title={isSub ? "Subtract stock" : "Add stock"} data-testid={testId}><Icon className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-sm p-3" data-testid={`${testId}-panel`}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{isSub ? "Subtract stock" : "Add stock"}</p>
        <div className="flex gap-2">
          <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} className="h-8 rounded-sm text-right tabular-nums" data-testid={`${testId}-qty`} />
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="h-8 w-28 rounded-sm" data-testid={`${testId}-unit`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cases">Cases</SelectItem>
              {casesPerPallet ? <SelectItem value="pallets">Pallets</SelectItem> : null}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          {isSub ? "Removes" : "Adds"} {Math.round(delta).toLocaleString()} cases {isSub ? "from" : "to"} stock
        </p>
        <Button size="sm" className={`mt-2 h-8 w-full rounded-sm text-white ${isSub ? "bg-red-600 hover:bg-red-700" : "bg-[#002FA7] hover:bg-[#002080]"}`} disabled={delta <= 0} onClick={() => { onApply(isSub ? -delta : delta); setOpen(false); setQty("1"); }} data-testid={`${testId}-apply`}>{isSub ? "Subtract from stock" : "Add to stock"}</Button>
      </PopoverContent>
    </Popover>
  );
}

const GRID = "grid-cols-[minmax(0,1fr)_minmax(0,1.9fr)_110px_110px_110px_120px_minmax(0,0.4fr)]";

function CpAdjust({ onApply, testId }) {
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (sign) => {
    const n = parseFloat(amt);
    if (!n || n <= 0) { toast.error("Enter a quantity first"); return; }
    setBusy(true);
    try { await onApply(sign * n); setAmt(""); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Input type="number" min="0" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="Qty" className="h-6 w-16 rounded-sm px-1.5 text-xs tabular-nums" data-testid={`${testId}-amt`} />
      <button type="button" disabled={busy} onClick={() => apply(-1)} title="Subtract cases" className="flex h-6 w-6 items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600" data-testid={`${testId}-minus`}>−</button>
      <button type="button" disabled={busy} onClick={() => apply(1)} title="Add cases" className="flex h-6 w-6 items-center justify-center rounded-sm border border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-600" data-testid={`${testId}-plus`}>+</button>
    </span>
  );
}

function CoPackerCell({ rows, testId, coPackers = [], onAdjust }) {
  const total = rows.reduce((a, r) => a + (r.qty || 0), 0);
  const [addCp, setAddCp] = useState("");
  const empty = !rows.length || Math.abs(total) < 0.01;
  if (empty && !onAdjust) return <span className="text-right text-sm text-slate-300">—</span>;
  return (
    <span className="text-right">
      <Popover>
        <PopoverTrigger asChild>
          <button className={`text-right text-sm font-semibold tabular-nums underline decoration-dotted underline-offset-2 ${empty ? "text-slate-300" : total < 0 ? "text-red-600" : "text-violet-700"}`} data-testid={testId}>
            {empty ? "—" : Math.round(total).toLocaleString()}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">At Co-Packers (Cases)</p>
          {rows.map((r, i) => (
            <div key={i} className="border-b border-slate-100 py-1 last:border-b-0">
              <div className="flex justify-between text-sm">
                <span className="truncate text-slate-600">{r.name}</span>
                <span className={`tabular-nums font-medium ${r.qty < 0 ? "text-red-600" : "text-slate-800"}`}>{Math.round(r.qty).toLocaleString()}</span>
              </div>
              {onAdjust && r.cpId && <div className="mt-1 flex justify-end"><CpAdjust testId={`${testId}-adj-${i}`} onApply={(d) => onAdjust(r.cpId, d)} /></div>}
            </div>
          ))}
          {onAdjust && coPackers.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-2">
              <Select value={addCp || undefined} onValueChange={setAddCp}>
                <SelectTrigger className="h-6 w-[110px] rounded-sm px-1.5 text-xs" data-testid={`${testId}-add-cp`}><SelectValue placeholder="Co-packer…" /></SelectTrigger>
                <SelectContent>{coPackers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}</SelectContent>
              </Select>
              <CpAdjust testId={`${testId}-add`} onApply={async (d) => { if (!addCp) { toast.error("Pick a co-packer first"); return; } await onAdjust(addCp, d); }} />
            </div>
          )}
          <Link to="/copacker-inventory" className="mt-2 inline-block text-xs font-semibold text-[#002FA7] hover:underline">Manage on Co-Packer Inventory →</Link>
        </PopoverContent>
      </Popover>
    </span>
  );
}

export default function FinishedGoodsInventory() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(false);
  const [mode, setMode] = useState("table");
  const [catFilter, setCatFilter] = useState("ALL");
  const [tagTarget, setTagTarget] = useState(null);
  const [tagSize, setTagSize] = useState("dk1202");
  const [tagForm, setTagForm] = useState({ material_number: "", batch: "", production_date: "", num_pallets: "", total_cases: "" });
  const [tagBusy, setTagBusy] = useState(false);
  const [tagHistory, setTagHistory] = useState([]);

  const findDesignRec = (did) => {
    if (!did) return null;
    for (const c of components) for (const t of c.types || []) for (const d of t.designs || []) if (d.id === did) return d;
    return null;
  };

  useEffect(() => {
    if (!tagTarget) return;
    setTagForm({ material_number: "", batch: "", production_date: "", num_pallets: "", total_cases: "" });
    api.get("/pallet-tags", { params: { product_id: tagTarget.product.id, design_id: tagTarget.design.id || "" } })
      .then(({ data }) => setTagHistory(data)).catch(() => setTagHistory([]));
  }, [tagTarget]);

  const tagJobs = useMemo(() => {
    const by = {};
    for (const r of tagHistory) (by[r.job_id] = by[r.job_id] || []).push(r);
    return Object.values(by)
      .map((rs) => rs.sort((a, b) => a.pallet_seq - b.pallet_seq))
      .sort((a, b) => (b[0].created_at || "").localeCompare(a[0].created_at || ""))
      .slice(0, 5);
  }, [tagHistory]);

  const outputTags = async (records, mode) => {
    const t = tagTarget;
    let company = {};
    try { company = (await api.get("/company-settings")).data; } catch { /* optional */ }
    const d = findDesignRec(t.design.id) || {};
    const r0 = records[0];
    const job = {
      size: tagSize, company,
      productName: r0.product_name || t.product.name, designName: r0.design_name || t.design.name || "",
      casePack: r0.case_pack, casesPerPallet: r0.cases_per_pallet,
      material: r0.material_number, batch: r0.batch, productionDate: r0.production_date,
      upc: r0.pallet_upc || d.pallet_upc || "", barcodeUrl: d.pallet_upc_barcode_url || "",
      pallets: records,
    };
    if (mode === "print") printPalletTags(job);
    else await downloadPalletTags(job);
  };

  const tagCasesError = (() => {
    const cpp = Number(tagTarget?.cpp || tagTarget?.cfg?.cases_per_pallet) || 0;
    const n = Number(tagForm.num_pallets) || 0;
    const total = tagForm.total_cases !== "" ? Number(tagForm.total_cases) : null;
    if (!cpp || !n || total == null) return "";
    if (total > cpp * n) return `${total.toLocaleString()} cases don't fit on ${n} pallet${n > 1 ? "s" : ""} at ${cpp} CS/pallet`;
    if (total <= cpp * (n - 1)) return `Too few cases — pallet ${n} would be empty`;
    return "";
  })();
  const tagFormValid = tagForm.material_number.trim() && tagForm.batch.trim() && tagForm.production_date && Number(tagForm.num_pallets) > 0 && !tagCasesError;

  const doGenerate = async (mode) => {
    const t = tagTarget;
    if (!t) return;
    if (!tagFormValid) { toast.error("Material #, Batch, Production Date and Number of Pallets are all required"); return; }
    setTagBusy(true);
    try {
      const d = findDesignRec(t.design.id) || {};
      const cpp = t.cpp || t.cfg.cases_per_pallet || null;
      const { data } = await api.post("/pallet-tags/generate", {
        product_id: t.product.id, design_id: t.design.id || "",
        product_name: t.product.name, design_name: t.design.name || "",
        case_pack: Number(t.cfg.bottles_per_case) || null,
        cases_per_pallet: Number(cpp) || null,
        material_number: tagForm.material_number.trim(), batch: tagForm.batch.trim(),
        production_date: tagForm.production_date,
        num_pallets: Number(tagForm.num_pallets),
        total_cases: tagForm.total_cases !== "" ? Number(tagForm.total_cases) : null,
        pallet_upc: d.pallet_upc || "",
      });
      await outputTags(data.pallets, mode);
      toast.success(`${data.pallets.length} pallet${data.pallets.length > 1 ? "s" : ""} · ${data.pallets.length * 2} tags generated`);
      setTagTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to generate pallet tags");
    } finally {
      setTagBusy(false);
    }
  };

  const reprintJob = async (records, mode) => {
    setTagBusy(true);
    try {
      await outputTags(records, mode);
    } catch {
      toast.error("Failed to reprint tags");
    } finally {
      setTagBusy(false);
    }
  };
  const [soResv, setSoResv] = useState({});
  const [soDlg, setSoDlg] = useState({ key: null, name: "" });
  const [soShip, setSoShip] = useState({});
  const [shipDlg, setShipDlg] = useState({ key: null, name: "" });
  const [copackerFG, setCopackerFG] = useState([]);

  const cpRows = useCallback((pid, did, casePack) => copackerFG
    .filter((s) => s.product_id === pid && (s.design_id || "") === (did || "") && Math.round(s.case_pack || 0) === Math.round(casePack || 0))
    .map((s) => ({ name: s.co_packer_name || "Co-Packer", qty: s.cases || 0, cpId: s.co_packer_id })), [copackerFG]);

  const [coPackers, setCoPackers] = useState([]);
  useEffect(() => { api.get("/co-packers").then((r) => setCoPackers(r.data || [])).catch(() => {}); }, []);
  const adjustCpFg = async (product, design, cfg, cpId, delta) => {
    try {
      await api.post("/copacker-fg/adjust", {
        co_packer_id: cpId, product_id: product.id, design_id: design?.id || "",
        case_pack: cfg.bottles_per_case, product_name: product.name, design_name: design?.name || "",
        delta, note: "Inline adjust from Finished Goods Inventory",
      });
      const cp = await api.get("/copacker-fg");
      setCopackerFG(cp.data || []);
      toast.success("Co-packer stock updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Adjustment failed");
    }
  };

  const fgKey = (pid, did, cp) => `${pid}::${did || ""}::${cp != null && cp !== "" ? Number(cp) : ""}`;

  const catOf = (p) => {
    if (p?.category) return p.category;
    const n = (p?.name || "").toUpperCase();
    return n.startsWith("PET") ? "PET" : n.startsWith("ALUM") ? "ALUM" : "";
  };

  const refresh = async () => {
    const [{ data }, r, sh] = await Promise.all([
      api.get("/finished-goods"),
      api.get("/sales-orders/reservations").catch(() => ({ data: { by_key: {} } })),
      api.get("/sales-orders/shipped-history").catch(() => ({ data: { by_key: {} } })),
    ]);
    setItems(data);
    setSoResv(r.data?.by_key || {});
    setSoShip(sh.data?.by_key || {});
  };

  useEffect(() => {
    (async () => {
      try {
        const [fg, prods, comps, r, cp, sh] = await Promise.all([api.get("/finished-goods"), api.get("/products"), api.get("/components"), api.get("/sales-orders/reservations"), api.get("/copacker-fg").catch(() => ({ data: [] })), api.get("/sales-orders/shipped-history").catch(() => ({ data: { by_key: {} } }))]);
        setItems(fg.data);
        setProducts(prods.data);
        setComponents(comps.data);
        setSoResv(r.data?.by_key || {});
        setSoShip(sh.data?.by_key || {});
        setCopackerFG(cp.data || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const usedFg = new Set();
    const fgFor = (pid, did, casePack) =>
      items.find((i) => i.product_id === pid && (i.design_id || "") === (did || "") && Number(i.case_pack) === Number(casePack));
    const gs = products.map((p, pi) => {
      const designs = [];
      const seen = new Set();
      (p.lines || []).forEach((l) => (l.designs || []).forEach((d) => { if (!seen.has(d.id)) { seen.add(d.id); designs.push(d); } }));
      designs.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      const cfgs = [...(p.pack_configs || [])].sort((a, b) => (a.bottles_per_case || 0) - (b.bottles_per_case || 0));
      const dlist = designs.length ? designs : [{ id: "", name: "" }];
      const sections = dlist.map((d) => ({
        design: d,
        rows: cfgs.map((c) => {
          const fg = fgFor(p.id, d.id, c.bottles_per_case);
          if (fg) usedFg.add(fg.id);
          return { key: `${p.id}:${d.id || "none"}:${c.id}`, cfg: c, fg };
        }),
      }));
      return { product: p, color: PALETTE[pi % PALETTE.length], sections };
    });
    // Legacy FG records that don't match any product/design/pack combination.
    // Hide retired pack sizes with nothing on hand (e.g. old 12-pack variants removed from the BOM).
    items.filter((i) => !usedFg.has(i.id) && Math.abs(i.qty_on_hand || 0) >= 0.01).forEach((i) => {
      const g = gs.find((x) => x.product.id === i.product_id);
      const section = {
        design: { id: i.design_id || "", name: i.design_name || "" },
        rows: [{ key: `fg-${i.id}`, cfg: { id: "", bottles_per_case: i.case_pack }, fg: i }],
      };
      if (g) g.sections.push(section);
      else gs.push({ product: { id: i.product_id, name: i.product_name }, color: "#94a3b8", sections: [section] });
    });
    const cgs = catFilter === "ALL" ? gs : gs.filter((g) => catOf(g.product) === catFilter);
    if (!term) return cgs;
    return cgs
      .map((g) => ({
        ...g,
        sections: g.sections
          .map((s) => ({
            ...s,
            rows: s.rows.filter((r) =>
              [g.product.name, s.design.name, `${r.cfg.bottles_per_case}-pack`, r.fg?.sku].some((v) => (v || "").toString().toLowerCase().includes(term))),
          }))
          .filter((s) => s.rows.length > 0),
      }))
      .filter((g) => g.sections.length > 0);
  }, [items, products, q, catFilter]);

  const adjust = async (product, design, cfg, delta) => {
    try {
      await api.post("/finished-goods/adjust", {
        product_id: product.id, design_id: design.id || "", design_name: design.name || "",
        config_id: cfg.id || "", delta,
      });
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const openHistory = async (fg) => {
    setDetail(fg);
    setLoadingHist(true);
    setHistory(null);
    try {
      const { data } = await api.get(`/finished-goods/${fg.id}/history`);
      setHistory(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoadingHist(false);
    }
  };

  const deleteManual = async (t) => {
    if (!window.confirm("Delete this manual addition? Finished goods will be removed and any consumed components restored.")) return;
    try {
      await api.delete(`/finished-goods/manual-add/${t.report_id}`);
      toast.success("Manual addition reversed.");
      const { data } = await api.get("/finished-goods");
      setItems(data);
      const still = data.find((i) => i.id === detail?.id);
      if (still) openHistory(still);
      else setDetail(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const stockCell = (product, design, row) => {
    const qty = Math.round(row.fg?.qty_on_hand || 0);
    const cpp = row.fg?.cases_per_pallet || row.cfg.cases_per_pallet || null;
    const pallets = cpp ? Math.floor(qty / cpp) : 0;
    const remCases = cpp ? qty - pallets * cpp : qty;
    return (
      <div className="flex items-center gap-2" data-testid={`fg-stock-cell-${row.key}`}>
        <div className="min-w-[80px] text-right leading-tight">
          <div className="tabular-nums text-sm font-semibold text-slate-800" data-testid={`fg-qty-${row.key}`}>
            {qty.toLocaleString()} <span className="text-[10px] font-normal text-slate-400">Cases</span>
          </div>
          {qty > 0 && cpp ? (
            <div className="whitespace-nowrap text-[10px] tabular-nums text-slate-400" data-testid={`fg-breakdown-${row.key}`}>
              {pallets.toLocaleString()} Pallet{pallets === 1 ? "" : "s"}{remCases > 0 ? ` · ${remCases.toLocaleString()} Case${remCases === 1 ? "" : "s"}` : ""}
            </div>
          ) : null}
        </div>
        <FGStockPopover mode="add" casesPerPallet={cpp} onApply={(d) => adjust(product, design, row.cfg, d)} testId={`fg-add-${row.key}`} />
        <FGStockPopover mode="subtract" casesPerPallet={cpp} onApply={(d) => adjust(product, design, row.cfg, d)} testId={`fg-subtract-${row.key}`} />
        {product.lines && (
          <CreateMOPopover
            products={products}
            components={components}
            initialProductId={product.id}
            initialCfgId={row.cfg.id || ""}
            initialDesignId={design.id || ""}
            onPlaced={refresh}
            testId={`fg-create-mo-${row.key}`}
          />
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Print pallet tag"
          onClick={() => setTagTarget({ product, design, cfg: row.cfg, cpp })} data-testid={`fg-pallet-tag-${row.key}`}>
          <Tag className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const printFG = (nonZeroOnly = false) => {
    const gs = groups
      .map((g) => ({
        name: g.product.name,
        rows: g.sections.flatMap((s) =>
          s.rows.map((row) => {
            const qty = Math.round(row.fg?.qty_on_hand || 0);
            const reserved = Math.round(soResv[fgKey(g.product.id, s.design.id, row.cfg.bottles_per_case)]?.reserved ?? row.fg?.reserved ?? 0);
            const cpp = row.fg?.cases_per_pallet || row.cfg.cases_per_pallet || null;
            const pallets = cpp ? Math.floor(qty / cpp) : 0;
            const rem = cpp ? qty - pallets * cpp : qty;
            const pack = row.cfg.bottles_per_case ? `${Math.round(row.cfg.bottles_per_case)}-Pack` : "—";
            return {
              name: s.design.name ? `${s.design.name} · ${pack}` : pack,
              stockText: `${qty.toLocaleString()} Cases${cpp && qty > 0 ? ` (${pallets.toLocaleString()} plt · ${rem.toLocaleString()} cs)` : ""}`,
              reserved,
              avail: qty - reserved,
              qty,
            };
          })
        ).filter((r) => !nonZeroOnly || r.qty > 0),
      }))
      .filter((g) => g.rows.length > 0);
    const catLabel = catFilter === "ALL" ? "" : ` (${catFilter})`;
    printInventoryReport(gs, nonZeroOnly ? `Finished Goods Inventory${catLabel} — In-Stock Items` : `Finished Goods Inventory${catLabel}`);
  };

  if (loading) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const exportExcel = () => {
    const rows = [];
    for (const g of groups) {
      for (const s of g.sections) {
        for (const r of s.rows) {
          const qty = Math.round(r.fg?.qty_on_hand || 0);
          const reserved = Math.round(soResv[fgKey(g.product.id, s.design.id, r.cfg.bottles_per_case)]?.reserved ?? r.fg?.reserved ?? 0);
          const cp = cpRows(g.product.id, s.design.id, r.cfg.bottles_per_case).reduce((a, x) => a + (x.qty || 0), 0);
          const shipped = Math.round(soShip[fgKey(g.product.id, s.design.id, r.cfg.bottles_per_case)]?.shipped || 0);
          rows.push({
            Product: g.product.name, Design: s.design.name || "",
            Pack: r.cfg.bottles_per_case ? `${Math.round(r.cfg.bottles_per_case)}-Pack` : "",
            "On Hand (Cases)": qty, "Reserved (SO)": reserved, Available: qty - reserved, "Shipped (Sales)": shipped, "At Co-Packers": cp,
          });
        }
      }
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 26 }, { wch: 26 }, { wch: 10 }, { wch: 14 }, { wch: 13 }, { wch: 11 }, { wch: 13 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Finished Goods");
    XLSX.writeFile(wb, `finished-goods-inventory-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div data-testid="finished-goods-inventory">
      <div className="mb-4 flex items-center justify-end gap-2">
        <button type="button" onClick={exportExcel}
          className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]"
          title="Download this inventory as an Excel file" data-testid="fg-export-excel-button">
          <Download className="h-4 w-4" /> Excel
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:text-[#002FA7]" data-testid="print-fg-button">
              <Printer className="h-4 w-4" /> Print
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-sm">
            <DropdownMenuItem onClick={() => printFG(false)} data-testid="print-fg-all">All finished goods</DropdownMenuItem>
            <DropdownMenuItem onClick={() => printFG(true)} data-testid="print-fg-nonzero">In-stock only (non-zero)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1">
          <button onClick={() => setMode("table")} className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "table" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="fg-view-table">Table View</button>
          <button onClick={() => setMode("shelf")} className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${mode === "shelf" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="fg-view-shelf">Shelf View</button>
        </div>
      </div>
      {mode === "shelf" ? <FGShelfView /> : (<>
      <div className="mb-4 inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="fg-category-filter">
        {["ALL", "PET", "ALUM"].map((c) => (
          <button key={c} type="button" onClick={() => setCatFilter(c)}
            className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${catFilter === c ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
            data-testid={`fg-category-filter-${c.toLowerCase()}`}>
            {c === "ALL" ? "All" : c}
          </button>
        ))}
      </div>
      <div className="mb-4 flex max-w-md items-center gap-2 rounded-sm border border-slate-300 px-3">
        <Search className="h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product, design or pack…"
          className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="fg-search" />
      </div>

      {groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 px-6 py-24 text-center" data-testid="fg-empty">
          <Warehouse className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">{q ? "No matches for your search." : catFilter !== "ALL" ? `No ${catFilter} products.` : "No products yet"}</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500">Finished goods stock rows come from your Product BOMs — each label design and case pack gets its own row.</p>
        </div>
      ) : (
        <div className="max-w-5xl border border-slate-200" data-testid="fg-table">
          <div className={`grid ${GRID} border-b border-slate-200 bg-slate-100 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-700`}>
            <span>Product / Design</span>
            <span>On Hand</span>
            <span className="text-right">Reserved (SO)</span>
            <span className="text-right">Available</span>
            <span className="text-right">Sales History</span>
            <span className="text-right">At Co-Packers</span>
            <span />
          </div>
          {groups.map((g) => (
            <div key={g.product.id} data-testid={`fg-group-${g.product.id}`}>
              <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2">
                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: g.color }} />
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{g.product.name}</span>
              </div>
              {g.sections.map((s, si) => (
                <div key={`${s.design.id || "none"}-${si}`}>
                  {s.design.name && (
                    <div className="border-b border-slate-100 bg-white px-4 py-1.5 pl-5 text-xs font-semibold text-slate-600">{s.design.name}</div>
                  )}
                  {s.rows.map((row) => {
                    const fg = row.fg;
                    const qty = Math.round(fg?.qty_on_hand || 0);
                    const rkey = fgKey(g.product.id, s.design.id, row.cfg.bottles_per_case);
                    const reserved = Math.round(soResv[rkey]?.reserved ?? fg?.reserved ?? 0);
                    const available = qty - reserved;
                    const shipped = Math.round(soShip[rkey]?.shipped || 0);
                    return (
                      <Fragment key={row.key}>
                        <div className={`grid ${GRID} items-center border-b border-slate-100 px-4 py-2.5`} data-testid={`fg-row-${row.key}`}>
                          <span className={`${s.design.name ? "pl-9" : "pl-5"} text-sm font-medium text-slate-800`}>
                            {row.cfg.bottles_per_case ? `${Math.round(row.cfg.bottles_per_case)}-Pack` : "—"}
                            {fg?.sku && <span className="ml-2 font-mono text-[10px] font-semibold text-[#002FA7]">{fg.sku}</span>}
                          </span>
                          {stockCell(g.product, s.design, row)}
                          <span className="text-right">
                            {reserved > 0 ? (
                              <button type="button" onClick={() => setSoDlg({ key: rkey, name: `${g.product.name}${s.design.name ? ` · ${s.design.name}` : ""} · ${Math.round(row.cfg.bottles_per_case)}pk`, product_id: g.product.id, design_id: s.design.id || "", case_pack: row.cfg.bottles_per_case })}
                                className="tabular-nums text-sm font-medium text-[#002FA7] hover:underline" data-testid={`fg-reserved-${row.key}`}>{reserved.toLocaleString()}</button>
                            ) : (
                              <span className="tabular-nums text-sm text-slate-400" data-testid={`fg-reserved-${row.key}`}>0</span>
                            )}
                          </span>
                          <span className="flex items-center justify-end gap-1 text-right">
                            {available < 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-red-600" data-testid={`fg-available-warning-${row.key}`} />}
                            <span className={`tabular-nums text-sm ${available < 0 ? "font-semibold text-red-600" : "text-slate-800"}`} data-testid={`fg-available-${row.key}`}>{available.toLocaleString()}</span>
                          </span>
                          <span className="text-right">
                            {shipped > 0 ? (
                              <button type="button" onClick={() => setShipDlg({ key: rkey, name: `${g.product.name}${s.design.name ? ` · ${s.design.name}` : ""} · ${Math.round(row.cfg.bottles_per_case)}pk` })}
                                className="tabular-nums text-sm font-medium text-emerald-700 hover:underline" data-testid={`fg-shipped-${row.key}`}>{shipped.toLocaleString()}</button>
                            ) : (
                              <span className="tabular-nums text-sm text-slate-400" data-testid={`fg-shipped-${row.key}`}>0</span>
                            )}
                          </span>
                          <CoPackerCell rows={cpRows(g.product.id, s.design.id, row.cfg.bottles_per_case)} testId={`fg-copacker-col-${row.key}`} coPackers={coPackers} onAdjust={(cpId, delta) => adjustCpFg(g.product, s.design, row.cfg, cpId, delta)} />
                          <span className="text-right">
                            {fg && (
                              <button onClick={() => openHistory(fg)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-[#002FA7]" data-testid={`fg-history-${fg.id}`}>
                                <History className="h-3.5 w-3.5" /> History
                              </button>
                            )}
                          </span>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      </>)}

      <SOReservationsDialog soKey={soDlg.key} soName={soDlg.name} fg={soDlg} onClose={() => setSoDlg({ key: null, name: "" })} onChanged={refresh} />
      <SalesHistoryDialog histKey={shipDlg.key} name={shipDlg.name} history={soShip} onClose={() => setShipDlg({ key: null, name: "" })} />

      <Dialog open={!!tagTarget} onOpenChange={(o) => { if (!o) setTagTarget(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="pallet-tag-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Print Pallet Tags</DialogTitle>
            <DialogDescription>
              {tagTarget ? `${tagTarget.product.name}${tagTarget.design.name ? ` — ${tagTarget.design.name}` : ""}${tagTarget.cfg?.bottles_per_case ? ` · ${tagTarget.cfg.bottles_per_case}-Pack` : ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          {tagTarget && (() => {
            const cpp = Number(tagTarget.cpp || tagTarget.cfg.cases_per_pallet) || 0;
            const n = Number(tagForm.num_pallets) || 0;
            const design = findDesignRec(tagTarget.design.id) || {};
            return (
              <div className="space-y-4">
                {!design.pallet_upc && (
                  <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700" data-testid="pallet-tag-upc-missing">
                    This design has no Pallet UPC — the tag will print without a UPC barcode. Add it on the design (SKUs &amp; UPCs) to include one.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Label Size</span>
                    <Select value={tagSize} onValueChange={setTagSize}>
                      <SelectTrigger className="rounded-sm" data-testid="pallet-tag-size"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(TAG_SIZES).map(([k, s]) => (<SelectItem key={k} value={k}>{s.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Material # <span className="text-red-600">*</span></span>
                    <Input value={tagForm.material_number} onChange={(e) => setTagForm((f) => ({ ...f, material_number: e.target.value }))} placeholder="e.g. 10001091" className="rounded-sm" data-testid="pallet-tag-material" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Batch <span className="text-red-600">*</span></span>
                    <Input value={tagForm.batch} onChange={(e) => setTagForm((f) => ({ ...f, batch: e.target.value }))} placeholder="e.g. 091922JX" className="rounded-sm" data-testid="pallet-tag-batch" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Production Date <span className="text-red-600">*</span></span>
                    <Input type="date" value={tagForm.production_date} onChange={(e) => setTagForm((f) => ({ ...f, production_date: e.target.value }))} className="rounded-sm" data-testid="pallet-tag-date" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Number of Pallets <span className="text-red-600">*</span></span>
                    <Input inputMode="numeric" value={tagForm.num_pallets} onChange={(e) => setTagForm((f) => ({ ...f, num_pallets: e.target.value.replace(/[^0-9]/g, "") }))} className="rounded-sm text-right tabular-nums" data-testid="pallet-tag-pallets" />
                  </div>
                  <div className="space-y-1">
                    <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Total Cases (optional)</span>
                    <Input inputMode="numeric" value={tagForm.total_cases} onChange={(e) => setTagForm((f) => ({ ...f, total_cases: e.target.value.replace(/[^0-9.]/g, "") }))} placeholder={cpp && n ? String(cpp * n) : ""} className={`rounded-sm text-right tabular-nums ${tagCasesError ? "border-red-400" : ""}`} data-testid="pallet-tag-total-cases" />
                    {tagCasesError
                      ? <p className="text-[11px] font-medium text-red-600" data-testid="pallet-tag-cases-error">{tagCasesError}</p>
                      : <p className="text-[11px] text-slate-400">{cpp ? `Blank = full pallets (${cpp} CS each). Enter the production quantity to auto-split a partial last pallet.` : "No cases/pallet on this pack config."}</p>}
                  </div>
                </div>
                {n > 0 && tagFormValid && (
                  <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="pallet-tag-preview">
                    <p className="font-semibold uppercase tracking-[0.1em] text-slate-500">Preview</p>
                    <p className="mt-1">Material # <b>{tagForm.material_number}</b> · Batch <b>{tagForm.batch}</b> · Prod. Date <b>{tagForm.production_date}</b></p>
                    <p>
                      Pallets: <b>{n}</b> · Unique SSCCs: <b>{n}</b> · Tags to Print: <b data-testid="pallet-tag-count">{n * 2}</b>
                      {tagForm.total_cases !== "" && cpp > 0 && Number(tagForm.total_cases) % cpp > 0 && Number(tagForm.total_cases) <= cpp * n && Number(tagForm.total_cases) > cpp * (n - 1)
                        ? <> · last pallet <b>{Math.round((Number(tagForm.total_cases) - cpp * (n - 1)) * 100) / 100} CS</b></> : null}
                    </p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" className="rounded-sm" onClick={() => setTagTarget(null)}>Cancel</Button>
                  <Button variant="outline" className="rounded-sm" disabled={tagBusy || !tagFormValid} onClick={() => doGenerate("download")} data-testid="pallet-tag-download">
                    {tagBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Generate &amp; Download
                  </Button>
                  <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={tagBusy || !tagFormValid} onClick={() => doGenerate("print")} data-testid="pallet-tag-print">
                    <Printer className="mr-1.5 h-4 w-4" /> Generate &amp; Print
                  </Button>
                </div>
                {tagJobs.length > 0 && (
                  <div className="border-t border-slate-200 pt-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Previous tag runs (reprint reuses the same SSCCs)</p>
                    <div className="mt-2 space-y-1">
                      {tagJobs.map((rs) => (
                        <div key={rs[0].job_id} className="flex items-center justify-between gap-2 rounded-sm border border-slate-100 px-2 py-1.5 text-xs text-slate-600" data-testid={`pallet-tag-job-${rs[0].job_id}`}>
                          <span className="truncate">
                            {(rs[0].created_at || "").slice(0, 10)} · Batch <b>{rs[0].batch}</b> · {rs.length} pallet{rs.length > 1 ? "s" : ""} · {rs.length > 1 ? <>SSCCs <b>…{rs[0].sscc.slice(-6)}</b> to <b>…{rs[rs.length - 1].sscc.slice(-6)}</b></> : <>SSCC <b>…{rs[0].sscc.slice(-6)}</b></>}
                          </span>
                          <span className="flex shrink-0 gap-1">
                            <Button variant="outline" size="sm" className="h-7 rounded-sm px-2 text-[11px]" disabled={tagBusy} onClick={() => reprintJob(rs, "download")} data-testid={`pallet-tag-redownload-${rs[0].job_id}`}>PDF</Button>
                            <Button variant="outline" size="sm" className="h-7 rounded-sm px-2 text-[11px]" disabled={tagBusy} onClick={() => reprintJob(rs, "print")} data-testid={`pallet-tag-reprint-${rs[0].job_id}`}>Reprint</Button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="fg-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{detail?.product_name} · {detail?.design_name || "—"} · {detail?.case_pack}-Pack</DialogTitle>
            <DialogDescription className="text-left">
              {detail?.sku} · On hand: {Math.round(detail?.qty_on_hand || 0).toLocaleString()} {detail?.unit}
            </DialogDescription>
          </DialogHeader>
          {loadingHist ? (
            <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (history?.transactions || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No production transactions yet.</p>
          ) : (
            <div className="space-y-3">
              {(history?.transactions || []).map((t) => (
                <div key={t.id} className="rounded-sm border border-slate-200 p-3 text-sm" data-testid={`fg-txn-${t.id}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-semibold ${t.change < 0 ? "text-red-600" : "text-emerald-600"}`}>{t.change < 0 ? "" : "+"}{Math.round(t.change).toLocaleString()} Cases</span>
                    <span className="text-xs text-slate-500">{fmtDate(t.date)} · Expires {expiryMeta(t.date).text} · Balance: {Math.round(t.balance).toLocaleString()}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                    <span>
                      {t.type === "manual_in" ? (
                        <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-700" data-testid={`fg-txn-manual-${t.id}`}>Manual addition{t.components?.length ? " · components consumed" : ""}</span>
                      ) : t.type === "manual_adjust" ? (
                        <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600" data-testid={`fg-txn-adjust-${t.id}`}>Manual adjustment</span>
                      ) : t.type === "shipped_to_customer" ? (
                        <span className="rounded-sm bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700" data-testid={`fg-txn-shipped-${t.id}`}>Shipped to customer{t.do_number ? ` · ${t.do_number}` : ""}</span>
                      ) : t.type === "copacker_transfer_in" ? (
                        <span className="rounded-sm bg-violet-100 px-1.5 py-0.5 font-semibold text-violet-700" data-testid={`fg-txn-transfer-${t.id}`}>Transfer in from co-packer{t.do_number ? ` · ${t.do_number}` : ""}</span>
                      ) : (
                        <>{t.mwo_number ? `MO ${moNum(t.mwo_number)}` : ""} {t.remainder ? `· ${t.remainder} remainder bottles` : ""}</>
                      )}
                    </span>
                    {t.type === "manual_in" && (
                      <button onClick={() => deleteManual(t)} className="font-medium text-red-500 hover:text-red-700" data-testid={`fg-txn-delete-${t.id}`}>
                        Delete &amp; reverse
                      </button>
                    )}
                  </div>
                  {t.components?.length > 0 && (
                    <div className="mt-2 border-t border-slate-100 pt-2">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Components consumed</p>
                      <table className="w-full table-fixed border-collapse text-xs">
                        <thead>
                          <tr className="text-[9px] uppercase text-slate-500">
                            <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Component</th>
                            <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Type</th>
                            <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {t.components.map((c, i) => (
                            <tr key={`${c.name}-${c.type || ""}-${i}`} data-testid={`fg-txn-comp-${t.id}-${i}`}>
                              <td className="border border-slate-300 px-2 py-1 font-semibold text-slate-800">{c.name}</td>
                              <td className="border border-slate-300 px-2 py-1 text-slate-500">{c.type || "—"}</td>
                              <td className="border border-slate-300 px-2 py-1 text-right font-medium text-red-600">-{Math.round(c.qty).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
